import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  CubeWorkflowData,
  GraphNode,
  OperationParameter,
  WRITE_DATA_FRAME_PARAMETER_NAMES,
  WriteDataFrameTransitFormGroup,
  WriteDataFrameTransitParameters,
  WriteDataFrameTransitRawParameters,
  WriteDataFrameTransitSaveMode,
} from '@selfai-platform/pipeline-common';
import { FormFieldComponentModule } from '@selfai-platform/pipeline-shared';
import { DialogService } from '@selfai-platform/shell';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { MessageModule } from 'primeng/message';
import { WorkflowEditorFacadeService } from '../../../../workflow-editor';
import { CubeDialogManagementService } from '../../../services/cube-dialog-management.service';
import { OperationDetailService } from '../../../services/operations';
import { DataSourceSelectorComponent } from '../../data-source-selector';
import { DialogHeaderService } from '../../dialog-header/dialog-header.service';

@Component({
  selector: 'selfai-platform-write-data-frame',
  templateUrl: './write-data-frame.component.html',
  styleUrl: './write-data-frame.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormFieldComponentModule,
    DataSourceSelectorComponent,
    CheckboxModule,
    ReactiveFormsModule,
    TranslateModule,
    MessageModule,
    DropdownModule,
  ],
  providers: [DialogHeaderService],
})
export class WriteDataFrameComponent implements OnInit {
  get node(): GraphNode<WriteDataFrameTransitRawParameters> | undefined {
    return this.dialogService?.data?.selectedNode;
  }

  get nodeId(): string {
    return this.node.id;
  }

  get hasParameters(): boolean {
    return Boolean(this.node.parameters);
  }

  saveModeOptions: WriteDataFrameTransitSaveMode[] = Object.values(WriteDataFrameTransitSaveMode);
  form: FormGroup<WriteDataFrameTransitFormGroup>;

  constructor(
    private readonly workflowEditorFacadeService: WorkflowEditorFacadeService,
    private readonly dialogService: DialogService<undefined, CubeWorkflowData<WriteDataFrameTransitRawParameters>>,
    private readonly dialogHeaderService: DialogHeaderService,
    private readonly cubeDialogManagementService: CubeDialogManagementService,
    private readonly translateService: TranslateService,
    private readonly operationDetailService: OperationDetailService,
  ) {}

  ngOnInit(): void {
    if (this.hasParameters) {
      const cubeParameters = this.node.parameters?.serialize();
      const { operationId } = this.node;

      this.operationDetailService.getWithLoading(operationId).subscribe({
        next: ({ parameters }) => {
          this.form = this.createFormGroup(parameters, cubeParameters);
        },
      });
    }

    this.dialogHeaderService.initCustomHeaderComponent(
      this.nodeId,
      this.translateService.instant('workflow.cubes.write-data-frame.modal-header'),
      this.onCloseDialog.bind(this),
      this.onSubmit.bind(this),
    );
    setTimeout(() => {
      this.cubeDialogManagementService.setFocus(this.nodeId);
    });
  }

  onSubmit(): void {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      this.workflowEditorFacadeService.updateNodeParamterValues({
        id: this.nodeId,
        parameters: this.normalizeFormValuesToApiModel(this.form.value as WriteDataFrameTransitParameters),
      });
      this.dialogService.close();
    }
  }

  onCloseDialog(): void {
    this.dialogService.close();
  }

  private normalizeFormValuesToApiModel(params: WriteDataFrameTransitParameters): WriteDataFrameTransitRawParameters {
    return {
      [WRITE_DATA_FRAME_PARAMETER_NAMES.DATA_SOURCE]: params.dataSourceId,
      [WRITE_DATA_FRAME_PARAMETER_NAMES.OVERWRITE]: params.overwrite,
      [WRITE_DATA_FRAME_PARAMETER_NAMES.SAVE_MODE]: params.saveMode ? { [params.saveMode]: {} } : null,
    };
  }

  // TODO: refactor this method after moved parameters parsing logic from AngularJS
  private createFormGroup(
    parameters: OperationParameter[],
    parametersSerialized: WriteDataFrameTransitRawParameters,
  ): FormGroup<WriteDataFrameTransitFormGroup> {
    const dataSourceId =
      parametersSerialized[WRITE_DATA_FRAME_PARAMETER_NAMES.DATA_SOURCE] ||
      (parameters.find((param) => param.name === WRITE_DATA_FRAME_PARAMETER_NAMES.DATA_SOURCE)?.default as string) ||
      null;
    const overwrite =
      parametersSerialized.overwrite ??
      (parameters.find((param) => param.name === WRITE_DATA_FRAME_PARAMETER_NAMES.OVERWRITE)?.default as boolean) ??
      null;

    const saveModeSerialized = parametersSerialized[WRITE_DATA_FRAME_PARAMETER_NAMES.SAVE_MODE];
    const saveMode = saveModeSerialized
      ? (Object.keys(saveModeSerialized)[0] as WriteDataFrameTransitSaveMode)
      : ((parameters.find((param) => param.name === WRITE_DATA_FRAME_PARAMETER_NAMES.SAVE_MODE)
          ?.default as WriteDataFrameTransitSaveMode) ?? null);

    return new FormGroup<WriteDataFrameTransitFormGroup>({
      dataSourceId: new FormControl(dataSourceId, [Validators.required]),
      overwrite: new FormControl(overwrite),
      saveMode: new FormControl(saveMode),
    });
  }
}
